import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import Stripe from "../components/Stripe";
import PayPal from "../components/PayPal";
import PayFast from "../components/PayFast";
import CyberSource from "../components/CyberSource";
import OfflinePayment from "../components/OfflinePayment";
import { useDispatch, useSelector } from "react-redux";
import { 
  FaCreditCard, 
  FaPaypal, 
  FaUniversity, 
  FaMoneyBillWave, 
  FaShieldAlt, 
  FaLock, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaArrowLeft,
  FaClock,
  FaMapMarkerAlt,
  FaUser
} from "react-icons/fa";
import { SiStripe, SiCashapp } from "react-icons/si";
import { MdOutlineSecurity } from "react-icons/md";
import { toast } from "react-hot-toast";
import api from '../api/api';

const Payment = ({ sellerId, sellerName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState(['stripe', 'paypal', 'eft']);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [cybersourceCurrency, setCybersourceCurrency] = useState('USD');
  const [paymentMethodOrder, setPaymentMethodOrder] = useState(['stripe', 'paypal', 'payfast', 'eft', 'cashOnDelivery']);

  // Set document title
  useEffect(() => {
    document.title = "4POS eCommerce Customer";
  }, []);

  // Extract orderSummary from location.state and store in component state
  useEffect(() => {
    // Debug logging
    console.log('Payment page - Navigation state:', location.state);
    console.log('Payment page - OrderSummary:', location.state?.orderSummary);
    
    // Check if we have valid data from navigation
    if (location.state && location.state.orderSummary) {
      console.log('Payment page - Setting order data:', location.state.orderSummary);
      setOrderData(location.state.orderSummary);
    } else {
      // Get seller ID from URL if possible
      const pathParts = location.pathname.split('/');
      const potentialSellerId = pathParts.length > 1 ? pathParts[1] : '';
      
      // Show error message and redirect to shipping
      toast.error('No order data found. Please complete your order from the beginning.');
      navigate(`/${sellerName}/shipping`);
    }
  }, [location, navigate, sellerName]);

  // Fetch enabled payment methods for the seller
  useEffect(() => {
    const fetchEnabledMethods = async () => {
      setLoadingPaymentMethods(true);
      try {
        // Get seller ID from props or order data
        const currentSellerId = sellerId || (orderData && orderData.sellerId);
        if (!currentSellerId) {
          console.error('No seller ID available');
          setEnabledPaymentMethods([]); // No fallback - show empty state
          setLoadingPaymentMethods(false);
          return;
        }

        // Fetch seller's enabled payment methods
        const { data } = await api.get(`/seller/${currentSellerId}/payment-methods`);

        
        if (data && data.success && data.enabledMethods) {
          setEnabledPaymentMethods(data.enabledMethods);
          
          // Set default payment method to first enabled
          if (data.enabledMethods.length > 0) {
            setPaymentMethod(data.enabledMethods[0]);
          }

          // Load payment method order if available
          if (data.paymentMethodOrder) {
            setPaymentMethodOrder(data.paymentMethodOrder);
          }
        } else {
          // No payment methods available
          setEnabledPaymentMethods([]);
        }
      } catch (err) {
        console.error('Error fetching seller payment methods:', err);
        // No fallback - show empty state
        setEnabledPaymentMethods([]);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };
    fetchEnabledMethods();
  }, [orderData, sellerId]);

  // Fetch seller payment configuration for EFT/Cash details
  useEffect(() => {
    const fetchPaymentConfig = async () => {
      const currentSellerId = sellerId || (orderData && orderData.sellerId);
      if (currentSellerId) {
        try {
          const { data } = await api.get(`/seller/${currentSellerId}/payment-config/public`);
          if (data && data.success) {
            setPaymentConfig(data.config);
            // Set CyberSource currency from config
            if (data.config.cybersource && data.config.cybersource.currency) {
              setCybersourceCurrency(data.config.cybersource.currency);
            }
          }
        } catch (err) {
          console.error('Error fetching payment config:', err);
          setPaymentConfig(null);
        }
      }
    };
    fetchPaymentConfig();
  }, [orderData, sellerId]);

  // Payment method configuration
  const paymentMethods = [
    {
      id: 'stripe',
      name: 'Credit/Debit Card',
      description: 'Secure payment with Stripe',
      icon: <SiStripe className="text-2xl" />,
      color: 'indigo',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      textColor: 'text-indigo-700',
      selectedBg: 'bg-indigo-600',
      selectedText: 'text-white'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      description: 'Pay with your PayPal account',
      icon: <FaPaypal className="text-2xl" />,
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      selectedBg: 'bg-blue-600',
      selectedText: 'text-white'
    },
    {
      id: 'payfast',
      name: 'PayFast Payment',
      description: '',
      icon: <img src="/images/payment/payfast.png" alt="PayFast" className="h-6 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />,
      color: 'orange',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700',
      selectedBg: 'bg-orange-600',
      selectedText: 'text-white'
    },
    {
      id: 'cybersource',
      name: 'CyberSource',
      description: 'Secure payment with CyberSource',
      icon: <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-xs">CS</div>,
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      selectedBg: 'bg-blue-600',
      selectedText: 'text-white'
    },
    {
      id: 'eft',
      name: 'Bank Transfer',
      description: 'EFT or bank transfer',
      icon: <FaUniversity className="text-2xl" />,
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
      selectedBg: 'bg-green-600',
      selectedText: 'text-white'
    },
    {
      id: 'cashOnDelivery',
      name: 'Cash on Delivery',
      description: 'Pay when you receive your order',
      icon: <FaMoneyBillWave className="text-2xl" />,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      textColor: 'text-emerald-700',
      selectedBg: 'bg-emerald-600',
      selectedText: 'text-white'
    }
  ];

  // If still loading, show a beautiful loading state
  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Headers sellerId={sellerId} sellerName={sellerName} />
        <div className="flex justify-center items-center min-h-[70vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Loading Payment</h2>
            <p className="text-gray-600">Preparing your secure payment experience...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Destructure order data once we know it exists
  const {
    subtotal = 0,
    shippingFee = 0,
    total = 0,
    currencySymbol = "NA",
    items = 0,
    orderNumber,
    shippingInfo = {},
  } = orderData;

  return (
    <div className="min-h-screen bg-gray-50">
      <Headers sellerId={sellerId} sellerName={sellerName} />
      
      <main className="py-8">
        <div className="w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto">
          
          {/* Checkout Progress Indicator */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Checkout Progress</h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <span className="ml-3 font-medium text-green-600">Shopping Cart</span>
                </div>
                <div className="flex-1 h-1 bg-gray-200 mx-4">
                  <div className="h-1 bg-green-500 w-full"></div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <span className="ml-3 font-medium text-green-600">Shipping Info</span>
                </div>
                <div className="flex-1 h-1 bg-gray-200 mx-4">
                  <div className="h-1 bg-green-500 w-full"></div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <span className="ml-3 font-medium text-orange-600">Payment</span>
                </div>
              </div>
            </div>
          </div>


          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <FaCreditCard className="text-2xl text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Complete Your Purchase
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto mb-4">
              Choose your preferred payment method to securely complete your order
            </p>
          </div>

          <div className="flex flex-wrap gap-8">
            {/* Payment Methods - Takes 67% width on desktop */}
            <div className="w-[67%] md-lg:w-full">
              <div className="pr-3 md-lg:pr-0">
                <div className="flex flex-col gap-6">
                  {/* Security Badge */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <MdOutlineSecurity className="text-xl text-green-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Secure Payment</h3>
                        <p className="text-sm text-gray-600">Your payment information is encrypted and secure with bank-level protection</p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <h2 className="text-xl font-bold text-gray-900">Choose Payment Method</h2>
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-600 text-xs font-medium rounded-full">
                        {enabledPaymentMethods.length} {enabledPaymentMethods.length === 1 ? 'option' : 'options'} available
                      </span>
                    </div>
                    
                    {loadingPaymentMethods ? (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mt-1"></div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-blue-800 mb-2">
                              Loading Payment Methods
                            </h3>
                            <p className="text-blue-700 text-sm mb-4">
                              Fetching available payment options for this seller...
                            </p>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                            </div>
                            <p className="text-blue-600 text-xs mt-2">Please wait while we load your payment options</p>
                          </div>
                        </div>
                      </div>
                    ) : enabledPaymentMethods.length === 0 ? (
                      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <FaExclamationTriangle className="h-6 w-6 text-yellow-500 mt-1" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-yellow-800 mb-2">
                              No Payment Methods Available
                            </h3>
                            <p className="text-yellow-700 text-sm">
                              This seller has not configured any payment methods for customers. 
                              Please contact the seller to enable payment options.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {paymentMethodOrder
                          .filter(methodId => enabledPaymentMethods.includes(methodId))
                          .map((methodId) => {
                            const method = paymentMethods.find(m => m.id === methodId);
                            if (!method) return null;
                          
                          const isSelected = paymentMethod === method.id;
                          
                          return (
                            <div
                              key={method.id}
                              className={`relative cursor-pointer rounded-lg border-2 transition-all duration-200 hover:shadow-md transform hover:scale-105 ${
                                isSelected
                                  ? `${method.selectedBg} ${method.selectedText} border-transparent shadow-lg scale-105`
                                  : `${method.bgColor} ${method.borderColor} hover:border-gray-300`
                              }`}
                              onClick={() => setPaymentMethod(method.id)}
                            >
                              <div className="p-5">
                                <div className="flex items-center justify-between mb-3">
                                  <div className={`p-2 rounded-lg ${
                                    isSelected 
                                      ? 'bg-white bg-opacity-20' 
                                      : `${method.bgColor} ${method.textColor}`
                                  }`}>
                                    <div className="text-2xl">
                                      {method.icon}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <FaCheckCircle className="text-white text-lg" />
                                  )}
                                </div>
                                <h3 className={`font-semibold text-lg mb-1 ${
                                  isSelected ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {method.name}
                                </h3>
                                <p className={`text-sm ${
                                  isSelected ? 'text-white text-opacity-90' : 'text-gray-600'
                                }`}>
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Payment Component */}
                    {enabledPaymentMethods.length > 0 && (
                      <div className="mt-8">
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="flex items-center gap-2 mb-4">
                            <FaLock className="text-green-600 text-sm" />
                            <span className="text-sm font-medium text-gray-700">Secure Payment Processing</span>
                          </div>
                          
                          {paymentMethod === "stripe" && (
                            <Stripe
                              orderNumber={orderNumber}
                              price={total}
                              sellerId={sellerId}
                              sellerName={sellerName}
                              shippingInfo={shippingInfo}
                              currencySymbol={currencySymbol}
                            />
                          )}
                          {paymentMethod === "paypal" && (
                            <PayPal
                              orderNumber={orderNumber}
                              price={total}
                              sellerId={sellerId}
                              sellerName={sellerName}
                              shippingInfo={shippingInfo}
                              currencySymbol={currencySymbol}
                            />
                          )}
                          {paymentMethod === "payfast" && (
                            <PayFast
                              orderNumber={orderNumber}
                              price={total}
                              sellerId={sellerId}
                              sellerName={sellerName}
                              shippingInfo={shippingInfo}
                              currencySymbol={currencySymbol}
                            />
                          )}
                          {paymentMethod === "cybersource" && (
                            <CyberSource
                              orderNumber={orderNumber}
                              price={total}
                              sellerId={sellerId}
                              sellerName={sellerName}
                              shippingInfo={shippingInfo}
                              currencySymbol={currencySymbol}
                              currency={cybersourceCurrency}
                            />
                          )}
                          {paymentMethod === "eft" && (
                            <OfflinePayment
                              orderNumber={orderNumber}
                              price={total}
                              sellerId={sellerId}
                              sellerName={sellerName}
                              bankDetails={paymentConfig?.bankTransfer?.bankDetails || ''}
                              instructions={paymentConfig?.bankTransfer?.instructions || ''}
                              isEFT={true}
                              currencySymbol={currencySymbol}
                            />
                          )}
                          {paymentMethod === "cashOnDelivery" && (
                            <OfflinePayment
                              orderNumber={orderNumber}
                              price={total}
                              sellerId={sellerId}
                              sellerName={sellerName}
                              bankDetails=""
                              instructions={paymentConfig?.cashOnDelivery?.instructions || ''}
                              isCashOnDelivery={true}
                              currencySymbol={currencySymbol}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary - Takes 30% width on desktop */}
            <div className="w-[30%] md-lg:w-full">
              <div className="pl-3 md-lg:pl-0 md-lg:mt-5">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <div>
                        <span className="text-gray-600">Subtotal ({items} Items)</span>
                      </div>
                      <span className="font-semibold">{currencySymbol}{subtotal.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">Shipping Fee</span>
                      <span className="font-semibold">{currencySymbol}{shippingFee.toFixed(2)}</span>
                    </div>
                    
                    <div className="pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-900">Total</span>
                        <span className="text-2xl font-bold text-indigo-600">{currencySymbol}{total.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Including all taxes and fees</p>
                    </div>
                  </div>

                  {/* Order Number */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 font-medium mb-1">Order Number</p>
                    <p className="font-mono text-sm font-semibold text-gray-900">{orderNumber}</p>
                  </div>

                  {/* Shipping Info */}
                  {shippingInfo && Object.keys(shippingInfo).length > 0 && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-3">
                        <FaMapMarkerAlt className="text-blue-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-blue-900 mb-1">Shipping Address</p>
                          <p className="text-xs text-blue-700">
                            {shippingInfo.address}, {shippingInfo.city}, {shippingInfo.country}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Security Notice */}
                  <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-start gap-3">
                      <FaShieldAlt className="text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-900 mb-1">Secure Checkout</p>
                        <p className="text-xs text-green-700">
                          Your payment information is protected with bank-level security
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Payment;